/**
 * Pure helpers for Settings → Compatibility Enforcement UI.
 * No I/O — safe for node:test.
 */

import { formatApiError } from '../../shared/lib/format-api-error.js';
import type { AuthUser } from '@/lib/types';
import type {
  CompatibilityPolicyDocumentDto,
  CompatibilityPolicyRegistryEntryDto,
  CompatibilityPolicyStateDto,
  CompatibilityPolicyValidationDto,
  CompatibilityPolicyVersionSummaryDto,
} from '../../shared/services/compatibility/compatibility-policy.dto.js';
import type { CompatibilityPolicyImpactPreviewDto } from '../../shared/services/compatibility/compatibility-policy-preview.dto.js';
import type {
  CompatibilityPolicyPromotionReadinessDto,
  CompatibilityPolicyPromotionStateDto,
  CompatibilityPolicyPromotionSurfaceStatusDto,
} from '../../shared/services/compatibility/compatibility-policy-promotion.dto.js';
import type {
  CompatibilityPolicyRuntimePromotionReadinessDto,
  CompatibilityPolicyRuntimeRolloutStateDto,
} from '../../shared/services/compatibility/compatibility-policy-runtime-promotion.dto.js';
import type { CompatibilityPolicyDocument } from '../../shared/services/compatibility/compatibility-policy.domain.js';
import {
  CONFIGURATION_PROMOTION_SURFACES,
  isPositivePromotionReadiness,
  type ConfigurationPromotionSurface,
} from '../../shared/services/compatibility/compatibility-policy-promotion.domain.js';
import {
  COMPATIBILITY_SURFACES,
  type CompatibilityEnforcement,
  type CompatibilitySurface,
} from '../../shared/services/compatibility/compatibility.domain.js';
import {
  COMPATIBILITY_CODES,
  type CompatibilityCode,
} from '../../shared/services/compatibility/compatibility-code-ids.js';

export const COMPATIBILITY_POLICY_VIEW = 'compatibility_policy.view';
export const COMPATIBILITY_POLICY_MANAGE = 'compatibility_policy.manage';
export const COMPATIBILITY_POLICY_PROMOTE = 'compatibility_policy.promote';
export const COMPATIBILITY_POLICY_RUNTIME_PROMOTE = 'compatibility_policy.runtime_promote';

export type CompatibilityPolicyTabId =
  | 'overview'
  | 'draft'
  | 'preview'
  | 'telemetry'
  | 'rollout'
  | 'runtime'
  | 'history';

export const COMPATIBILITY_POLICY_TABS: Array<{ id: CompatibilityPolicyTabId; label: string }> = [
  { id: 'overview', label: 'Status' },
  { id: 'draft', label: 'Proposed policy' },
  { id: 'preview', label: 'What would change' },
  { id: 'telemetry', label: 'What we’ve seen' },
  { id: 'rollout', label: 'Apply to admin checks' },
  { id: 'runtime', label: 'Apply when sending' },
  { id: 'history', label: 'Version history' },
];

/** Where operators define report-type ↔ template/wrapper/AI-profile pairings (not this Settings page). */
export const COMPATIBILITY_DEFINITION_HUB_LINKS = [
  {
    href: '/settings.html#report-type-compatibility',
    label: 'Report type compatibility hub',
    description:
      'See assembly capabilities (read-only), set AI defaults for Incident/Outage, and jump to template associations for each report type.',
  },
  {
    href: '/templates.html',
    label: 'Edit template & wrapper compatibility',
    description:
      'Choose which report types each report template, email wrapper, and SMS template supports. Also edit Report type wrappers.',
  },
  {
    href: '/ai-profiles.html',
    label: 'Edit AI summary profile compatibility',
    description:
      'Map each report type to the AI summary profile it should use. Profiles are scenario-specific (for example an Incident intrusion prompt must not be applied to an Outage).',
  },
  {
    href: '/settings.html#scenarios-delivery',
    label: 'Edit delivery presets by report type',
    description: 'Set default report, email, and SMS templates — and the AI summary profile — used when seeding delivery rules.',
  },
  {
    href: '/report-delivery.html',
    label: 'Fix a delivery rule’s templates & AI profile',
    description: 'Change templates or the AI summary override on an individual delivery rule when a pairing is wrong.',
  },
] as const;

export const COMPATIBILITY_LEAVE_ALONE_MESSAGE =
  'You can leave this alone. Built-in rules already protect saves and most checks.';

/** Display order for operational rollout (audit → preview → validation → save → runtime). */
export const PROMOTION_SURFACE_DISPLAY_ORDER: readonly CompatibilitySurface[] = [
  ...CONFIGURATION_PROMOTION_SURFACES,
  'runtime',
];

export const SURFACE_LABELS: Record<CompatibilitySurface, string> = {
  preview: 'Preview',
  save: 'Save',
  validation: 'Validation',
  audit: 'Audit',
  runtime: 'When sending',
};

/** Short purpose under each surface name. */
export const SURFACE_PURPOSE_LABELS: Record<CompatibilitySurface, string> = {
  preview: 'While previewing a delivery rule in admin',
  save: 'When saving a rule, profile, or wrapper assignment',
  validation: 'In configuration validation / health checks',
  audit: 'In Tools audits and review findings',
  runtime: 'When a report is generated or delivered',
};

/** Longer “how it works” copy for Proposed Policy cells. */
export const SURFACE_HOW_IT_WORKS: Record<CompatibilitySurface, string> = {
  preview:
    'Controls notices shown when you preview a delivery rule before saving. Does not stop a save by itself.',
  save:
    'Controls whether a bad pairing can be saved. Block = the form refuses to save; Warn = save succeeds with a warning; Allow = save ignores this issue.',
  validation:
    'Controls how severe this issue looks in configuration validation runs (Action Center / Tools validation).',
  audit:
    'Controls how this issue is reported in audit-style Tools scans. Usually softer than Save.',
  runtime:
    'Controls what happens during real report generation and send. Highest impact — can skip a channel or stop delivery.',
};

export const ENFORCEMENT_LABELS: Record<CompatibilityEnforcement, string> = {
  allow: 'Allow',
  warn: 'Warn',
  block: 'Block',
};

export const ENFORCEMENT_HOW_IT_WORKS: Record<CompatibilityEnforcement, string> = {
  allow: 'Ignore this issue in this place — no block and no warning.',
  warn: 'Show a warning, but do not stop the action.',
  block: 'Stop the action (or treat it as a hard error) until the pairing is fixed.',
};

/** Plain-language badge copy for Status → fix-list severity (save-surface enforcement). */
export const FIX_LIST_SEVERITY_LABELS: Record<CompatibilityEnforcement, string> = {
  allow: 'Allowed on save',
  warn: 'Warns on save',
  block: 'Blocks saves',
};

export type CompatibilityFixListItem = {
  key: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  parentLabel: string | null;
  summary: string;
  remediation: string | null;
  /** Worst live enforcement among grouped save-surface findings. */
  severity: CompatibilityEnforcement;
  href: string;
  linkLabel: string;
  issueCodes: string[];
};

/**
 * Build a deduplicated remediations list from impact-preview findings.
 * Prefer Save-surface rows (what operators hit when editing); fall back to any surface.
 */
export function buildCompatibilityFixList(
  findings: Array<{
    entityType: string;
    entityId: string;
    entityLabel: string;
    parentLabel?: string | null;
    summary: string;
    remediation?: string | null;
    code: string;
    surface: CompatibilitySurface;
    state: string;
    currentOperationalEnforcement: CompatibilityEnforcement;
  }>,
): CompatibilityFixListItem[] {
  const actionable = findings.filter(
    (f) =>
      f.state === 'incompatible'
      || f.currentOperationalEnforcement === 'block'
      || f.currentOperationalEnforcement === 'warn',
  );

  const byEntityRaw = new Map<string, typeof actionable>();
  for (const finding of actionable) {
    const key = `${finding.entityType}:${finding.entityId}`;
    const list = byEntityRaw.get(key) ?? [];
    list.push(finding);
    byEntityRaw.set(key, list);
  }

  const byEntity = new Map<string, CompatibilityFixListItem>();
  const severityRank: Record<CompatibilityEnforcement, number> = {
    allow: 0,
    warn: 1,
    block: 2,
  };

  for (const [key, group] of byEntityRaw) {
    const saveRows = group.filter((f) => f.surface === 'save');
    const source = saveRows.length > 0 ? saveRows : group;
    const first = source[0]!;
    const href =
      entityFixHref(first.entityType, first.entityId)
      ?? entityDefinitionHref(first.entityType)
      ?? '/templates.html';
    const linkLabel =
      entityFixLinkLabel(first.entityType)
      ?? entityDefinitionLinkLabel(first.entityType)
      ?? 'Open';
    const item: CompatibilityFixListItem = {
      key,
      entityType: first.entityType,
      entityId: first.entityId,
      entityLabel: first.entityLabel,
      parentLabel: first.parentLabel ?? null,
      summary: first.summary,
      remediation: first.remediation ?? null,
      severity: first.currentOperationalEnforcement,
      href,
      linkLabel,
      issueCodes: [],
    };
    for (const finding of source) {
      if (!item.issueCodes.includes(finding.code)) {
        item.issueCodes.push(finding.code);
      }
      if (severityRank[finding.currentOperationalEnforcement] > severityRank[item.severity]) {
        item.severity = finding.currentOperationalEnforcement;
        item.summary = finding.summary;
        item.remediation = finding.remediation ?? item.remediation;
      }
    }
    byEntity.set(key, item);
  }

  return [...byEntity.values()].sort((a, b) => {
    const sev = severityRank[b.severity] - severityRank[a.severity];
    if (sev !== 0) return sev;
    return a.entityLabel.localeCompare(b.entityLabel);
  });
}

/** Deep link that opens the specific broken entity when possible. */
export function entityFixHref(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
): string | null {
  const type = (entityType ?? '').toLowerCase();
  const id = (entityId ?? '').trim();
  switch (type) {
    case 'delivery_rule':
    case 'delivery-rule':
    case 'rule':
      return id ? `/report-delivery.html?ruleId=${encodeURIComponent(id)}` : '/report-delivery.html';
    case 'report_template':
      return id ? `/templates.html?tab=report&reportId=${encodeURIComponent(id)}` : '/templates.html?tab=report';
    case 'delivery_template':
    case 'email_wrapper':
      return id
        ? `/templates.html?tab=email&wrapperId=${encodeURIComponent(id)}&channel=email`
        : '/templates.html?tab=email';
    case 'sms_wrapper':
      return id
        ? `/templates.html?tab=sms&wrapperId=${encodeURIComponent(id)}&channel=sms`
        : '/templates.html?tab=sms';
    case 'report_type_profile':
    case 'profile':
      return '/settings.html#scenarios-delivery';
    case 'ai_summary_profile':
    case 'ai_profile':
      return '/ai-profiles.html';
    case 'system_default_wrapper':
    case 'system_default':
      return '/templates.html?tab=type-wrappers';
    default:
      return null;
  }
}

export function entityFixLinkLabel(entityType: string | null | undefined): string | null {
  switch ((entityType ?? '').toLowerCase()) {
    case 'delivery_rule':
    case 'delivery-rule':
    case 'rule':
      return 'Open this delivery rule';
    case 'report_template':
      return 'Open this report template';
    case 'delivery_template':
    case 'email_wrapper':
      return 'Open this email wrapper';
    case 'sms_wrapper':
      return 'Open this SMS template';
    case 'report_type_profile':
    case 'profile':
      return 'Open delivery presets';
    case 'ai_summary_profile':
    case 'ai_profile':
      return 'Open AI Profiles';
    case 'system_default_wrapper':
    case 'system_default':
      return 'Open Report type wrappers';
    default:
      return null;
  }
}

/** Deep link for an impact-preview / telemetry entity type. */
export function entityDefinitionHref(entityType: string | null | undefined): string | null {
  switch ((entityType ?? '').toLowerCase()) {
    case 'delivery_rule':
    case 'delivery-rule':
    case 'rule':
      return '/report-delivery.html';
    case 'report_template':
    case 'delivery_template':
    case 'email_wrapper':
    case 'sms_wrapper':
    case 'template':
    case 'wrapper':
      return '/templates.html';
    case 'report_type_profile':
    case 'profile':
      return '/settings.html#scenarios-delivery';
    case 'ai_summary_profile':
    case 'ai_profile':
      return '/ai-profiles.html';
    case 'system_default_wrapper':
    case 'system_default':
      return '/templates.html?tab=type-wrappers';
    default:
      return null;
  }
}

export function entityDefinitionLinkLabel(entityType: string | null | undefined): string | null {
  const href = entityDefinitionHref(entityType);
  if (!href) return null;
  if (href.includes('report-delivery')) return 'Open Delivery Rules';
  if (href.includes('scenarios-delivery')) return 'Open delivery presets';
  if (href.includes('ai-profiles')) return 'Open AI Profiles';
  if (href.includes('tab=type-wrappers')) return 'Open Report type wrappers';
  return 'Open Templates';
}

export function prospectiveClassificationLabel(value: string): string {
  switch (value) {
    case 'would_warn':
      return 'Would have warned';
    case 'would_block':
      return 'Would have blocked';
    case 'would_relax':
      return 'Would have been less strict';
    case 'unknown_incomplete':
      return 'Could not compare (incomplete)';
    case 'same':
      return 'Same as live behavior';
    default:
      return value.replaceAll('_', ' ');
  }
}

export function runtimeImpactScopeLabel(value: string): string {
  switch (value) {
    case 'channel':
      return 'Email or SMS channel';
    case 'render':
      return 'Report render';
    case 'package':
      return 'Delivery package';
    default:
      return value.replaceAll('_', ' ');
  }
}

export function previewImpactLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

export function previewEvidenceLabel(value: string): string {
  switch (value) {
    case 'static_only':
      return 'From current config only';
    case 'historically_exercised':
      return 'Seen in past activity';
    case 'insufficient':
      return 'Not enough evidence';
    case 'not_applicable':
      return 'Not applicable';
    default:
      return value.replaceAll('_', ' ');
  }
}

export function runtimeRolloutStatusLabel(value: string): string {
  switch (value) {
    case 'enabled':
      return 'Applied';
    case 'disabled':
      return 'Built-in rules';
    case 'suspended':
      return 'Suspended';
    case 'not_ready':
      return 'Not ready';
    default:
      return value.replaceAll('_', ' ');
  }
}

export function canViewCompatibilityPolicy(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.permissions?.includes(COMPATIBILITY_POLICY_VIEW));
}

export function canManageCompatibilityPolicy(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.permissions?.includes(COMPATIBILITY_POLICY_MANAGE));
}

export function canPromoteCompatibilityPolicy(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.permissions?.includes(COMPATIBILITY_POLICY_PROMOTE));
}

export function canRuntimePromoteCompatibilityPolicy(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.permissions?.includes(COMPATIBILITY_POLICY_RUNTIME_PROMOTE));
}

export function promotionSourceLabel(
  source: CompatibilityPolicyPromotionSurfaceStatusDto['source'],
): string {
  switch (source) {
    case 'stored_policy':
      return 'Stored policy';
    case 'code_defaults':
      return 'Built-in rules';
    case 'unavailable':
      return 'Unavailable';
    default:
      return source;
  }
}

export function promotionRiskLabel(
  risk: CompatibilityPolicyPromotionSurfaceStatusDto['risk'],
): string {
  switch (risk) {
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'high':
      return 'High';
    case 'highest':
      return 'Highest';
    default:
      return risk;
  }
}

export function promotionEnableActionLabel(surface: ConfigurationPromotionSurface): string {
  return `Apply stored policy to ${SURFACE_LABELS[surface]}`;
}

export function promotionDisableActionLabel(surface: ConfigurationPromotionSurface): string {
  return `Return ${SURFACE_LABELS[surface]} to built-in rules`;
}

export function promotionSnapshotHealthLabel(
  health: CompatibilityPolicyPromotionStateDto['rolloutSnapshotHealth'],
): string {
  switch (health) {
    case 'loaded':
      return 'Rollout state loaded';
    case 'code_defaults':
      return 'All admin checks use built-in rules';
    case 'db_unavailable':
      return 'Database unavailable — apply state unknown';
    case 'refresh_failed_stale':
      return 'Refresh failed — showing last known apply state';
    default:
      return health;
  }
}

export const PROMOTION_READINESS_UNAVAILABLE_MESSAGE =
  'Readiness unavailable — apply is disabled';

export type PromotionEnableGateResult = {
  canSubmit: boolean;
  /** True when disable/rollback UI may still be offered (subject to promote permission). */
  canDisable: boolean;
  reasons: string[];
  matchedReadiness: CompatibilityPolicyPromotionReadinessDto | null;
};

/**
 * Fail-closed enable gate for Operational Rollout.
 * Never treats null/loading/error/stale readiness as permission to promote.
 */
export function evaluatePromotionEnableGate(input: {
  surface: ConfigurationPromotionSurface | null | undefined;
  canPromote: boolean;
  readiness: CompatibilityPolicyPromotionReadinessDto | null | undefined;
  readinessLoadState: 'idle' | 'loading' | 'loaded' | 'error';
  promotionState: CompatibilityPolicyPromotionStateDto | null | undefined;
  busy: string | null | undefined;
}): PromotionEnableGateResult {
  const reasons: string[] = [];
  const surface = input.surface ?? null;
  const canDisable = Boolean(input.canPromote && surface && input.promotionState?.revision != null);

  if (!surface) {
    reasons.push('No promotion surface selected.');
    return { canSubmit: false, canDisable: false, reasons, matchedReadiness: null };
  }
  if (!input.canPromote) {
    reasons.push('Permission to apply stored policy to admin checks is required.');
  }
  if (!input.promotionState) {
    reasons.push('Apply-to-admin-checks state has not loaded.');
  } else if (input.promotionState.revision == null) {
    reasons.push('Apply-state revision is missing.');
  }
  if (input.promotionState?.storageActivePolicyVersionId == null) {
    reasons.push('Save a policy for review before applying it to admin checks.');
  }
  if (
    input.promotionState?.rolloutSnapshotHealth === 'db_unavailable'
    || input.promotionState?.rolloutSnapshotHealth === 'refresh_failed_stale'
  ) {
    reasons.push(PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
  }

  if (input.readinessLoadState === 'loading' || input.busy === `promotion-readiness:${surface}`) {
    reasons.push('Readiness is still loading.');
  }
  if (input.readinessLoadState === 'idle') {
    reasons.push('Readiness has not been requested.');
  }
  if (input.readinessLoadState === 'error') {
    reasons.push(PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
  }

  const matched =
    input.readiness && input.readiness.surface === surface ? input.readiness : null;
  if (!matched) {
    if (input.readinessLoadState === 'loaded') {
      reasons.push('Readiness response is absent or for a different surface.');
    }
    return {
      canSubmit: false,
      canDisable,
      reasons: reasons.length > 0 ? reasons : [PROMOTION_READINESS_UNAVAILABLE_MESSAGE],
      matchedReadiness: null,
    };
  }

  if (!isPositivePromotionReadiness(matched)) {
    if (matched.status === 'unavailable') {
      reasons.push(PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
    } else if (matched.ready !== true) {
      reasons.push('Readiness is not ready.');
    }
    if (matched.blockers.length > 0) {
      reasons.push(...matched.blockers);
    }
  }
  if (!matched.preview.fresh) {
    reasons.push('Impact preview evidence is stale or missing.');
  }
  if (!matched.preview.token || !matched.preview.generatedAt) {
    reasons.push('Required preview token is absent.');
  }
  if (input.busy === `promotion-enable:${surface}`) {
    reasons.push('An apply change is already pending.');
  }

  const canSubmit = reasons.length === 0;
  return { canSubmit, canDisable, reasons, matchedReadiness: matched };
}

/**
 * Enable dialog may open only when readiness is conclusively ready for the surface.
 * Loading/error/unavailable/not-ready/incomplete evidence must not open the dialog
 * (blockers remain visible in the readiness detail card instead).
 * Submit remains gated by {@link evaluatePromotionEnableGate} plus acknowledgments.
 */
export function canOpenPromotionEnableDialog(input: {
  surface: ConfigurationPromotionSurface | null | undefined;
  canPromote: boolean;
  readiness: CompatibilityPolicyPromotionReadinessDto | null | undefined;
  readinessLoadState: 'idle' | 'loading' | 'loaded' | 'error';
}): boolean {
  if (!input.canPromote || !input.surface) return false;
  if (input.readinessLoadState !== 'loaded') return false;
  const matched =
    input.readiness && input.readiness.surface === input.surface ? input.readiness : null;
  if (!matched) return false;
  return (
    isPositivePromotionReadiness(matched)
    && matched.preview.fresh === true
    && Boolean(matched.preview.token && matched.preview.generatedAt)
  );
}

export function isPromotionDisableAvailable(input: {
  surface: ConfigurationPromotionSurface | null | undefined;
  canPromote: boolean;
  promotionState: CompatibilityPolicyPromotionStateDto | null | undefined;
  enabled: boolean;
}): boolean {
  return Boolean(
    input.canPromote
    && input.surface
    && input.enabled
    && input.promotionState
    && input.promotionState.revision != null,
  );
}

export const RUNTIME_PROMOTION_READINESS_UNAVAILABLE_MESSAGE =
  'Readiness unavailable — apply when sending is disabled';

export type RuntimePromotionEnableGateResult = {
  canSubmit: boolean;
  reasons: string[];
  matchedReadiness: CompatibilityPolicyRuntimePromotionReadinessDto | null;
};

function isPositiveRuntimePromotionReadiness(input: {
  status: 'ready' | 'not_ready' | 'unavailable';
  ready: boolean;
  blockers: string[];
}): boolean {
  return input.status === 'ready' && input.ready === true && input.blockers.length === 0;
}

function runtimeTelemetryRequired(
  readiness: CompatibilityPolicyRuntimePromotionReadinessDto,
): boolean {
  const min = readiness.runtimeSemantics?.minimumEvidence;
  if (!min) return false;
  return min.observationWindowDays > 0 || min.minEvents > 0 || min.minUniqueExecutions > 0;
}

/**
 * Fail-closed enable gate for Runtime Enforcement (per-code).
 * Never treats null/loading/error readiness as permission to enforce.
 */
export function evaluateRuntimePromotionEnableGate(input: {
  code: string | null | undefined;
  canRuntimePromote: boolean;
  readiness: CompatibilityPolicyRuntimePromotionReadinessDto | null | undefined;
  readinessLoadState: 'idle' | 'loading' | 'loaded' | 'error';
  runtimeRolloutState?: CompatibilityPolicyRuntimeRolloutStateDto | null | undefined;
  busy: string | null | undefined;
}): RuntimePromotionEnableGateResult {
  const reasons: string[] = [];
  const code = input.code ?? null;

  if (!code) {
    reasons.push('No compatibility code selected.');
    return { canSubmit: false, reasons, matchedReadiness: null };
  }
  if (!input.canRuntimePromote) {
    reasons.push('Permission to apply strictness when sending reports is required.');
  }
  if (
    input.runtimeRolloutState?.rolloutSnapshotHealth === 'db_unavailable'
    || input.runtimeRolloutState?.rolloutSnapshotHealth === 'refresh_failed_stale'
  ) {
    reasons.push(RUNTIME_PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
  }

  if (input.readinessLoadState === 'loading' || input.busy === `runtime-readiness:${code}`) {
    reasons.push('Readiness is still loading.');
  }
  if (input.readinessLoadState === 'idle') {
    reasons.push('Readiness has not been requested.');
  }
  if (input.readinessLoadState === 'error') {
    reasons.push(RUNTIME_PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
  }

  const matched =
    input.readiness && input.readiness.code === code ? input.readiness : null;
  if (!matched) {
    if (input.readinessLoadState === 'loaded') {
      reasons.push('Readiness response is absent or for a different code.');
    }
    return {
      canSubmit: false,
      reasons: reasons.length > 0 ? reasons : [RUNTIME_PROMOTION_READINESS_UNAVAILABLE_MESSAGE],
      matchedReadiness: null,
    };
  }

  if (!matched.promotable) {
    reasons.push('This issue type cannot be applied when sending reports.');
  }
  if (!matched.runtimeSemantics) {
    reasons.push('Send-time behavior details are absent.');
  }
  if (!isPositiveRuntimePromotionReadiness(matched)) {
    if (matched.status === 'unavailable') {
      reasons.push(RUNTIME_PROMOTION_READINESS_UNAVAILABLE_MESSAGE);
    } else if (matched.ready !== true) {
      reasons.push('Readiness is not ready.');
    }
    if (matched.blockers.length > 0) {
      reasons.push(...matched.blockers);
    }
  }
  if (!matched.preview.available || !matched.preview.fresh) {
    reasons.push('Impact preview evidence is stale or missing.');
  }
  if (!matched.preview.token || !matched.preview.generatedAt) {
    reasons.push('Required preview token is absent.');
  }
  if (runtimeTelemetryRequired(matched) && !matched.telemetry.healthy) {
    reasons.push('Observed-impact data is unhealthy for this issue type.');
  }
  if (!matched.applicationCompatibility.compatible) {
    reasons.push('This app version cannot safely apply this send-time change.');
  }
  if (
    input.busy === `runtime-enable:${code}`
    || input.busy === `runtime-disable:${code}`
  ) {
    reasons.push('A send-time apply change is already pending.');
  }

  const canSubmit = reasons.length === 0;
  return { canSubmit, reasons, matchedReadiness: matched };
}

/**
 * Enable dialog may open only when readiness is conclusively ready for the code.
 */
export function canOpenRuntimePromotionEnableDialog(input: {
  code: string | null | undefined;
  canRuntimePromote: boolean;
  readiness: CompatibilityPolicyRuntimePromotionReadinessDto | null | undefined;
  readinessLoadState: 'idle' | 'loading' | 'loaded' | 'error';
}): boolean {
  if (!input.canRuntimePromote || !input.code) return false;
  if (input.readinessLoadState !== 'loaded') return false;
  const matched =
    input.readiness && input.readiness.code === input.code ? input.readiness : null;
  if (!matched || !matched.promotable || !matched.runtimeSemantics) return false;
  return (
    isPositiveRuntimePromotionReadiness(matched)
    && matched.preview.available === true
    && matched.preview.fresh === true
    && Boolean(matched.preview.token && matched.preview.generatedAt)
    && matched.applicationCompatibility.compatible === true
    && (!runtimeTelemetryRequired(matched) || matched.telemetry.healthy === true)
  );
}

/**
 * Disable remains available with runtime_promote even when readiness is unavailable.
 */
export function evaluateRuntimePromotionDisableGate(input: {
  canRuntimePromote: boolean;
  mutationPending?: boolean;
  busy?: string | null | undefined;
  code?: string | null | undefined;
}): { canDisable: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (!input.canRuntimePromote) {
    reasons.push('Permission to apply strictness when sending reports is required.');
  }
  const mutationPending =
    input.mutationPending === true
    || (input.code != null
      && (input.busy === `runtime-enable:${input.code}`
        || input.busy === `runtime-disable:${input.code}`));
  if (mutationPending) {
    reasons.push('A send-time apply change is already pending.');
  }
  return { canDisable: reasons.length === 0, reasons };
}

export function runtimeCodeDisplayLabel(code: string, fallbackLabel?: string | null): string {
  if (fallbackLabel) return fallbackLabel;
  return code
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function runtimeEnableActionLabel(
  code: string,
  impactScope: string,
): string {
  switch (code) {
    case COMPATIBILITY_CODES.EMAIL_WRAPPER_TYPE_MISMATCH:
      return 'Enforce Email Wrapper Blocks at Runtime';
    case COMPATIBILITY_CODES.SMS_WRAPPER_TYPE_MISMATCH:
      return 'Block Incompatible SMS Wrapper Channel';
    case COMPATIBILITY_CODES.TEMPLATE_INACTIVE:
      return 'Enforce Template-Inactive Render Blocks at Runtime';
    case COMPATIBILITY_CODES.TEMPLATE_NOT_FOUND:
      return 'Enforce Template-Not-Found Render Blocks at Runtime';
    default: {
      const label = runtimeCodeDisplayLabel(code);
      if (impactScope === 'channel') {
        return `Block Incompatible ${label} Channel`;
      }
      if (impactScope === 'render') {
        return `Enforce ${label} Render Blocks at Runtime`;
      }
      if (impactScope === 'package') {
        return `Enforce ${label} Package Blocks at Runtime`;
      }
      return `Enforce ${label} at Runtime`;
    }
  }
}

export function runtimeDisableActionLabel(label: string): string {
  return `Return ${label} to built-in send-time rules`;
}

export function anyOperationalSurfaceEnabled(
  surfaces: Record<CompatibilitySurface, boolean> | null | undefined,
): boolean {
  if (!surfaces) return false;
  return PROMOTION_SURFACE_DISPLAY_ORDER.some((surface) => surfaces[surface] === true);
}

export function clonePolicyDocument(doc: CompatibilityPolicyDocumentDto): CompatibilityPolicyDocument {
  return {
    schemaVersion: 1,
    overrides: structuredClone(doc.overrides ?? {}),
  };
}

export function emptyPolicyDocument(): CompatibilityPolicyDocument {
  return { schemaVersion: 1, overrides: {} };
}

export function countOverrides(doc: CompatibilityPolicyDocumentDto | null | undefined): number {
  if (!doc?.overrides) return 0;
  let n = 0;
  for (const surfaces of Object.values(doc.overrides)) {
    if (!surfaces) continue;
    n += Object.keys(surfaces).length;
  }
  return n;
}

export function documentsEqual(
  a: CompatibilityPolicyDocumentDto | null | undefined,
  b: CompatibilityPolicyDocumentDto | null | undefined,
): boolean {
  return JSON.stringify(a?.overrides ?? {}) === JSON.stringify(b?.overrides ?? {});
}

/** Reset to code default removes the sparse override rather than storing a duplicate. */
export function setSparseOverride(
  doc: CompatibilityPolicyDocumentDto,
  code: string,
  surface: CompatibilitySurface,
  nextEnforcement: CompatibilityEnforcement | null,
  codeDefault: CompatibilityEnforcement,
): CompatibilityPolicyDocument {
  const next = clonePolicyDocument(doc);
  const codeKey = code as CompatibilityCode;
  const surfaceMap: Partial<Record<CompatibilitySurface, CompatibilityEnforcement>> = {
    ...(next.overrides[codeKey] ?? {}),
  };

  if (nextEnforcement == null || nextEnforcement === codeDefault) {
    delete surfaceMap[surface];
  } else {
    surfaceMap[surface] = nextEnforcement;
  }

  if (Object.keys(surfaceMap).length === 0) {
    delete next.overrides[codeKey];
  } else {
    next.overrides[codeKey] = surfaceMap;
  }
  return next;
}

export function getOverrideEnforcement(
  doc: CompatibilityPolicyDocumentDto | null | undefined,
  code: string,
  surface: CompatibilitySurface,
): CompatibilityEnforcement | null {
  const cell = doc?.overrides?.[code as CompatibilityCode]?.[surface];
  return cell ?? null;
}

export function filterRegistryEntries(
  registry: CompatibilityPolicyRegistryEntryDto[],
  filters: {
    query?: string;
    dimension?: string | null;
    configurableOnly?: boolean;
    overridesOnly?: boolean;
    draft?: CompatibilityPolicyDocumentDto | null;
  },
): CompatibilityPolicyRegistryEntryDto[] {
  const q = (filters.query ?? '').trim().toLowerCase();
  return registry.filter((entry) => {
    if (filters.configurableOnly && !entry.configurable) return false;
    if (filters.dimension && entry.dimension !== filters.dimension) return false;
    if (filters.overridesOnly) {
      const surfaces = filters.draft?.overrides?.[entry.code];
      if (!surfaces || Object.keys(surfaces).length === 0) return false;
    }
    if (!q) return true;
    return (
      entry.code.toLowerCase().includes(q)
      || entry.label.toLowerCase().includes(q)
      || entry.description.toLowerCase().includes(q)
      || (entry.dimension ?? '').toLowerCase().includes(q)
    );
  });
}

export function operationalSurfacesDisabled(
  state: Pick<CompatibilityPolicyStateDto, 'operationalApplication'> | null | undefined,
): boolean {
  if (!state) return true;
  const s = state.operationalApplication.surfaces;
  return COMPATIBILITY_SURFACES.every((surface) => s[surface] === false);
}

export type ActivationGateResult = {
  ok: boolean;
  reasons: string[];
};

export function evaluateActivationReadiness(input: {
  canManage: boolean;
  dirty: boolean;
  hasDraft: boolean;
  draftRevision: string | null;
  validation: CompatibilityPolicyValidationDto | null;
  validationStale: boolean;
  currentRegistryFingerprint: string;
}): ActivationGateResult {
  const reasons: string[] = [];
  if (!input.canManage) reasons.push('Permission to edit proposed policies is required.');
  if (!input.hasDraft) reasons.push('Create and save a proposed policy first.');
  if (input.dirty) reasons.push('Save unsaved proposed changes before saving for review.');
  if (!input.validation || input.validationStale) {
    reasons.push('Run structural validation on the current saved proposal.');
  } else if (!input.validation.valid || !input.validation.validationToken) {
    reasons.push('Proposed policy validation must succeed before saving for review.');
  } else if (input.validation.registryFingerprint !== input.currentRegistryFingerprint) {
    reasons.push('Built-in rule catalog changed — re-validate the proposal.');
  } else if (input.draftRevision && input.validation.draftRevision !== input.draftRevision) {
    reasons.push('Validation revision does not match the saved proposal.');
  }
  return { ok: reasons.length === 0, reasons };
}

export function isValidationStale(input: {
  validation: CompatibilityPolicyValidationDto | null;
  draftRevision: string | null;
  dirty: boolean;
  registryFingerprint: string;
}): boolean {
  if (!input.validation) return true;
  if (input.dirty) return true;
  if (input.draftRevision && input.validation.draftRevision !== input.draftRevision) return true;
  if (input.validation.registryFingerprint !== input.registryFingerprint) return true;
  return false;
}

export function isPreviewStaleForDraft(input: {
  preview: CompatibilityPolicyImpactPreviewDto | null;
  draftId: number | null;
  draftRevision: string | null;
  dirty: boolean;
  registryFingerprint: string;
}): boolean {
  if (!input.preview) return false;
  if (input.dirty) return true;
  if (input.preview.policy.targetType !== 'draft') return false;
  if (input.draftId != null && input.preview.policy.versionId !== input.draftId) return true;
  if (input.draftRevision && input.preview.policy.draftRevision !== input.draftRevision) return true;
  if (input.preview.policy.registryFingerprint !== input.registryFingerprint) return true;
  return false;
}

export function primaryPolicyAction(input: {
  canManage: boolean;
  hasDraft: boolean;
  dirty: boolean;
  validation: CompatibilityPolicyValidationDto | null;
  validationStale: boolean;
  preview: CompatibilityPolicyImpactPreviewDto | null;
  previewStale: boolean;
}): 'create_draft' | 'save_draft' | 'fix_validation' | 'run_preview' | 'activate' | 'view_only' {
  if (!input.canManage) return 'view_only';
  if (!input.hasDraft) return 'create_draft';
  if (input.dirty) return 'save_draft';
  if (!input.validation || input.validationStale) return 'run_preview';
  if (!input.validation.valid) return 'fix_validation';
  if (!input.preview || input.previewStale) return 'run_preview';
  return 'activate';
}

export function versionStatusLabel(status: CompatibilityPolicyVersionSummaryDto['status']): string {
  switch (status) {
    case 'draft':
      return 'Proposed';
    case 'active':
      return 'Saved for review';
    case 'superseded':
      return 'Superseded';
    case 'discarded':
      return 'Discarded';
    default:
      return status;
  }
}

export function healthLabel(health: CompatibilityPolicyStateDto['health']['snapshotHealth']): string {
  switch (health) {
    case 'active_loaded':
      return 'Stored policy loaded';
    case 'code_defaults':
      return 'Using built-in rules';
    case 'invalid_rejected':
      return 'Invalid stored policy rejected — using built-in rules';
    case 'db_unavailable':
      return 'Database unavailable — last known or built-in rules';
    case 'refresh_failed_stale':
      return 'Refresh failed — showing last known state';
    default:
      return health;
  }
}

export function parseApiErrorMessage(err: unknown): { status: number | null; message: string } {
  if (err && typeof err === 'object' && 'status' in err && 'message' in err) {
    const status = typeof (err as { status: unknown }).status === 'number'
      ? (err as { status: number }).status
      : null;
    let message = String((err as { message: unknown }).message ?? 'Request failed');
    try {
      const parsed = JSON.parse(message) as { error?: string; message?: string };
      // The policy editor also uses these codes to identify stale revisions.
      if (parsed.error) message = parsed.error;
      else if (parsed.message) message = parsed.message;
    } catch {
      message = formatApiError(Object.assign(new Error(message), { status }));
    }
    return { status, message };
  }
  if (err instanceof Error) return { status: null, message: formatApiError(err) };
  return { status: null, message: 'Request failed' };
}

export function conflictImpliesStaleRevision(status: number | null, message: string): boolean {
  if (status !== 409) return false;
  const lower = message.toLowerCase();
  return lower.includes('conflict') || lower.includes('revision') || lower.includes('updated');
}
